import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Printer, Heart, Users, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

/**
 * /partner-promo/:code — printable one-page promo flyer for partner
 * agencies. Single A4/Letter sheet, designed to be handed out by
 * rescues / NGOs / breeders to potential members. The agency's code
 * is the prominent call-to-action.
 *
 * The print stylesheet hides the screen-only "Print" toolbar and
 * removes shadows / hover effects so the flyer prints cleanly.
 */
export default function PartnerPromoFlyer() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/partner-codes/lookup/${encodeURIComponent(code)}`);
        if (mounted) setInfo(res.data);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || 'Code not found');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4">
        <div>
          <h1 className="text-2xl font-heading font-bold mb-2">Code not found</h1>
          <p className="text-muted-foreground">
            The code <code className="bg-muted px-1 rounded">{code}</code> isn&rsquo;t valid or has been deactivated.
          </p>
        </div>
      </div>
    );
  }

  const redeemUrl = `https://americandogclub.com/redeem/${encodeURIComponent(info.code)}`;
  // QR via free public endpoint — no third-party JS, prints crisply.
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(redeemUrl)}`;

  return (
    <div className="min-h-screen bg-neutral-100 print:bg-white">
      <SEO
        title={`Promo flyer — ${info.partner_name || info.code}`}
        description="Printable AADC partner promo flyer."
        url={`/partner-promo/${info.code}`}
        noindex
      />

      {/* Screen-only toolbar */}
      <div className="bg-white border-b print:hidden">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            One-page promo for <strong>{info.partner_name || 'your organization'}</strong>. Print or save as PDF.
          </p>
          <Button onClick={() => window.print()} data-testid="promo-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      {/* The flyer — sized for A4/Letter when printed */}
      <div className="py-10 print:py-0">
        <article
          className="bg-white max-w-3xl mx-auto px-10 py-12 print:py-12 shadow-lg print:shadow-none rounded-lg print:rounded-none"
          data-testid="promo-flyer"
          style={{ minHeight: '11in' }}
        >
          <header className="border-b-4 border-amber-700 pb-5 mb-7">
            <p className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold mb-1">
              A gift from {info.partner_name || 'our partner'}
            </p>
            <h1 className="text-4xl font-heading font-bold text-neutral-900">
              Welcome to the All American Dog Club
            </h1>
            <p className="text-neutral-600 mt-2">
              A member-governed online community for dog people, dog clubs, breeders, and rescues.
            </p>
          </header>

          {/* Code + QR side-by-side */}
          <section className="flex items-stretch gap-6 mb-8">
            <div className="flex-1 rounded-xl border-2 border-amber-300 bg-amber-50 p-6 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-wider text-amber-800 font-semibold mb-2">
                Your code
              </p>
              <p className="font-mono font-bold text-3xl tracking-wider text-amber-900 break-all" data-testid="promo-code">
                {info.code}
              </p>
              <p className="text-sm text-amber-800 mt-3">
                Redeem this code at{' '}
                <span className="font-semibold">americandogclub.com/redeem</span>{' '}
                for <strong>{info.free_months} free month{info.free_months > 1 ? 's' : ''}</strong>{' '}
                of paid AADC membership.
              </p>
            </div>
            <div className="flex flex-col items-center justify-center">
              <img
                src={qrSrc}
                alt={`QR code for redeeming ${info.code}`}
                className="w-40 h-40"
              />
              <p className="text-xs text-neutral-500 mt-2 text-center">
                Or scan to redeem
              </p>
            </div>
          </section>

          {/* Three pillars */}
          <section className="grid grid-cols-3 gap-5 mb-8">
            <Pillar
              icon={<Users />}
              title="Community"
              body="Connect with dog people across the country. Share advice, photos, and local events. Join breed-specific Communities."
            />
            <Pillar
              icon={<Heart />}
              title="Real impact"
              body="A portion of every paid membership goes to dog welfare organizations chosen by the membership."
            />
            <Pillar
              icon={<ShieldCheck />}
              title="Trust"
              body="Member-governed, no data sold to advertisers, optional identity verification keeps bots and trolls out."
            />
          </section>

          {/* How to redeem */}
          <section className="mb-8">
            <h2 className="text-lg font-heading font-bold mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" /> How to redeem
            </h2>
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-neutral-700">
              <li>
                Visit <strong>americandogclub.com/redeem</strong> in any browser, or scan the QR code.
              </li>
              <li>
                Enter the code <span className="font-mono font-bold">{info.code}</span> exactly as shown.
              </li>
              <li>
                Sign up for a free AADC account (or log in if you have one).
              </li>
              <li>
                Your <strong>{info.free_months} free month{info.free_months > 1 ? 's' : ''}</strong> of paid membership are added immediately.
              </li>
            </ol>
          </section>

          <footer className="border-t pt-4 text-xs text-neutral-500 flex items-center justify-between">
            <p>
              Distributed by <strong>{info.partner_name || 'AADC partner'}</strong>.
              {info.partner_type && <span className="ml-1">({info.partner_type})</span>}
            </p>
            <p>americandogclub.com</p>
          </footer>
        </article>
      </div>
    </div>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="w-9 h-9 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center mb-2">
        {React.cloneElement(icon, { className: 'w-5 h-5' })}
      </div>
      <h3 className="font-heading font-semibold text-sm mb-1">{title}</h3>
      <p className="text-xs text-neutral-600 leading-snug">{body}</p>
    </div>
  );
}
